import { PDFDocument, PDFTextField, PDFCheckBox, PDFDropdown, PDFRadioGroup } from "pdf-lib";

// ── Prettify an AcroForm field name into a readable label ────────────────────
// "Weather_7AM_Condition" → "Weather 7Am Condition"
// "ProjectLocation"       → "Project Location"
function prettifyFieldName(name) {
  return name
    .replace(/([a-z])([A-Z])/g, "$1 $2")   // camelCase split
    .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2")
    .replace(/[_.\-]/g, " ")                // separator split
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase()); // Title Case
}

// ── Infer a section name from an AcroForm field name ─────────────────────────
// Uses the first segment before "_" or "." as the section prefix.
// Falls back to "General Info".
function inferSection(fieldName) {
  const sep = fieldName.indexOf("_") !== -1 ? "_" : fieldName.indexOf(".") !== -1 ? "." : null;
  if (!sep) return "General Info";
  const prefix = fieldName.split(sep)[0];
  // Ignore single-char or numeric prefixes
  if (prefix.length < 2 || /^\d+$/.test(prefix)) return "General Info";
  return prettifyFieldName(prefix);
}

// ── Map AcroForm field type to template field type ───────────────────────────
function getFieldType(field, name) {
  if (field instanceof PDFCheckBox) return "checkbox";
  if (field instanceof PDFDropdown || field instanceof PDFRadioGroup) return "select";
  if (/\bdate\b/i.test(name)) return "date";
  return "text";
}

// ── Parse a fillable PDF and return a template object ────────────────────────
export async function importPdfAsTemplate(file, arrayBuffer) {
  const pdfDoc = await PDFDocument.load(new Uint8Array(arrayBuffer), { ignoreEncryption: true });
  const form = pdfDoc.getForm();
  const pdfFields = form.getFields();

  if (pdfFields.length === 0) {
    throw new Error("No fillable form fields found in this PDF. Make sure it is an AcroForm (fillable PDF).");
  }

  // Group fields by inferred section
  const sectionMap = new Map();
  for (const pdfField of pdfFields) {
    const name = pdfField.getName();
    const type = getFieldType(pdfField, name);
    let options = "";
    if (pdfField instanceof PDFDropdown) {
      try { options = pdfField.getOptions().join(","); } catch (_) {}
    }

    const section = inferSection(name);
    if (!sectionMap.has(section)) sectionMap.set(section, []);
    sectionMap.get(section).push({
      // Use AcroForm field name as field ID so export can find the field by ID
      id: name,
      label: prettifyFieldName(name),
      type,
      options,
      defaultValue: "",
    });
  }

  const id = crypto.randomUUID();
  const sections = Array.from(sectionMap.entries()).map(([secName, fields]) => ({
    id: crypto.randomUUID(),
    name: secName,
    fields,
  }));

  return {
    id,
    name: file.name.replace(/\.pdf$/i, ""),
    description: "",
    isPdf: true,
    sections,
  };
}

// ── Shared: load PDF, fill fields from report, return bytes ──────────────────
async function buildFilledPdf(rawBytes, report, template) {
  const pdfDoc = await PDFDocument.load(rawBytes);
  const form = pdfDoc.getForm();

  for (const sec of template.sections || []) {
    for (const field of sec.fields || []) {
      const val = report[field.id];
      if (val === undefined || val === null || val === "") continue;
      try {
        const pdfField = form.getField(field.id);
        if (pdfField instanceof PDFCheckBox) {
          const checked = val === true || val === "true" || val === "Yes" || val === "yes";
          checked ? pdfField.check() : pdfField.uncheck();
        } else if (pdfField instanceof PDFDropdown) {
          pdfField.select(String(val));
        } else if (pdfField instanceof PDFRadioGroup) {
          pdfField.select(String(val));
        } else {
          // PDFTextField (and anything else)
          pdfField.setText(String(val));
        }
      } catch (_) {
        // Field name not found or type mismatch — skip silently
      }
    }
  }

  return pdfDoc.save();
}

// ── Download a filled copy of the PDF ─────────────────────────────────────────
export async function exportFilledPdf(rawBytes, report, template) {
  const bytes = await buildFilledPdf(rawBytes, report, template);
  const blob = new Blob([bytes], { type: "application/pdf" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${(report.reportTitle || template.name || "report").replace(/[^a-zA-Z0-9_\-. ]/g, "_")}.pdf`;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 10000);
}

// ── Return a blob URL of the filled PDF (for iframe preview) ─────────────────
export async function getFilledPdfBlobUrl(rawBytes, report, template) {
  const bytes = await buildFilledPdf(rawBytes, report, template);
  const blob = new Blob([bytes], { type: "application/pdf" });
  return URL.createObjectURL(blob);
}
